import { useSelector } from 'react-redux';
import { ENABLE_FULL_RESPONSIVE } from 'reactApp/appHelpers/configHelpers';
import { BREAKPOINT_SM } from 'reactApp/constants/breakpoints';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { useActionsConfig } from 'reactApp/hooks/useActionsConfig';
import { getFilesWithSelectedFace } from 'reactApp/modules/faces/faces.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import {
    getCurrentTotalCount,
    getItemsTotalCount,
    getStorageCurrentFolder,
    getStorageSelectedItems,
} from 'reactApp/modules/storage/storage.selectors';
import { CloudItem, EActions, EStorageType } from 'reactApp/modules/storage/storage.types';
import { getIsLetterGroup } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.selectors';
import { RootState } from 'reactApp/store';
import { ListItem } from 'reactApp/ui/DropdownList/DropdownList';
import { actionsOrder, actionsOrder_PdfEdit, moreListOrder, TOOLBAR_CONFIG } from 'reactApp/ui/Toolbar/Toolbar.config';
import { getCloneItem, getDownloadItem, getSelectItem, toolbarDropdownItems, toolbarItems } from 'reactApp/ui/Toolbar/Toolbar.data';
import { ToolbarContextEnum, TToolbarItem } from 'reactApp/ui/Toolbar/Toolbar.types';
import { useWindowWidth } from 'reactApp/ui/VirtualList/VirtualList.hooks';

const getPriority = (item) => (typeof item == 'number' ? item : undefined);

export const useActions = ({
    storage,
    toolbarType,
    downloadButtonRef,
}: {
    storage: EStorageType;
    toolbarType?: ToolbarContextEnum;
    downloadButtonRef?: React.RefObject<HTMLDivElement>;
}): TToolbarItem[] => {
    const settings = TOOLBAR_CONFIG[storage];

    const items: CloudItem[] = useSelector((state: RootState) => getStorageSelectedItems(state, storage)) as CloudItem[];
    const filesWithFaces = useSelector(getFilesWithSelectedFace);
    const quotaCleanerIsLetterGroup = useSelector((state: RootState) => getIsLetterGroup(state));
    const currentFolder = useSelector((state: RootState) => getStorageCurrentFolder(state, storage));

    const { isHome, isQuotaCleaner, isPublic, isStock } = getStorage(storage);
    const isRootHome = isHome && currentFolder?.id === ROOT_FOLDER_ID;
    const totalItemsCount = useSelector((state: RootState) => getCurrentTotalCount(state, undefined, undefined));
    const selectedCount = items.length;
    const allSelected = !!selectedCount && selectedCount === totalItemsCount;

    const itemsAllCount = useSelector(getItemsTotalCount);

    const availableActions = useActionsConfig();
    const windowWidth = useWindowWidth();

    if (!settings || toolbarType === ToolbarContextEnum.pageContentContext) {
        return [];
    }

    const actions: TToolbarItem[] = (storage === EStorageType.pdfEdit ? actionsOrder_PdfEdit : actionsOrder)
        .filter((action) => availableActions[action] && Boolean(toolbarItems[action]) && Boolean(settings[action]))
        .map((action) => {
            let item: TToolbarItem = toolbarItems[action];

            if (action === EActions.download) {
                const isDownloadOff = isQuotaCleaner && quotaCleanerIsLetterGroup;
                if (isDownloadOff) {
                    return null;
                }

                item = getDownloadItem(items, filesWithFaces, currentFolder as CloudItem, isRootHome, downloadButtonRef);
            } else if (action === EActions.selectAll) {
                item = getSelectItem({ allSelected, itemsAllCount, itemsCount: selectedCount });
            } else if (action === EActions.clone) {
                item = getCloneItem(isPublic || isStock);
            }

            if (
                ENABLE_FULL_RESPONSIVE &&
                windowWidth <= BREAKPOINT_SM &&
                (action === EActions.remove || action === EActions.publish || action === EActions.share)
            ) {
                return null;
            }

            return {
                ...item,
                priority: getPriority(settings[action]),
            };
        })
        .filter((el) => !!el) as TToolbarItem[];

    const moreList: ListItem[] = moreListOrder
        .filter((action) => availableActions[action] && Boolean(toolbarDropdownItems[action]))
        .map((action) => toolbarDropdownItems[action]);

    if (moreList.length) {
        if (ENABLE_FULL_RESPONSIVE && windowWidth <= BREAKPOINT_SM) {
            if (availableActions[EActions.publish]) {
                moreList.push(toolbarItems.publish as ListItem);
            }

            if (availableActions[EActions.share]) {
                moreList.push(toolbarItems.share as ListItem);
            }

            if (availableActions[EActions.remove]) {
                moreList.push(toolbarItems.remove as ListItem);
            }
        }

        actions.push({
            ...toolbarItems[EActions.more],
            list: moreList,
        });
    }

    return actions;
};
